import { useNotification } from '../../shared/notification/useNotification';
import { useTranslation } from 'react-i18next';
import { ForwardingType } from '../models/Forwarding/Forwarding';
import { BehApiRoutes, behClient } from '../beh-api';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export interface DestinationConfig {
  name: string;
  forwardingType: ForwardingType;
  publicServiceIdentifier: string;
  forwardingTargetConfig: {
    destinationId: string;
    publicServiceIdentifier: string;
  };
}

interface CreateDestinationConfig {
  orgUnitId: string;
  destinationConfig: DestinationConfig;
}

export function useOrganizationForwardingPost() {
  const queryClient = useQueryClient();
  const { notifyUserFromRequest, notifyUser } = useNotification();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ destinationConfig, orgUnitId }: CreateDestinationConfig) =>
      notifyUserFromRequest(
        () =>
          behClient.post(
            BehApiRoutes.forwardingsByOrgUnit(orgUnitId),
            destinationConfig
          ),
        {
          errorMessage: t('api.error.createFitConnectSenderCreds'),
          successMessage: t('api.success.createFitConnectSenderCreds'),
          errorIdHandler: {
            'behoerdenclient/problems/illegalForwardingConfig/selfForwardingIsNotAllowed':
              () => {
                notifyUser({
                  intent: 'error',
                  title: t(
                    'api.error.forwardingConfig.selfForwardingIsNotAllowed'
                  )
                });
              },
            'behoerdenclient/problems/illegalForwardingConfig/forwardingTargetDestinationNotFound':
              () => {
                notifyUser({
                  intent: 'error',
                  title: t(
                    'api.error.forwardingConfig.forwardingTargetDestinationNotFound'
                  )
                });
              },
            'behoerdenclient/problems/illegalForwardingConfig/forwardingTargetPublicServiceNotFound':
              () => {
                notifyUser({
                  intent: 'error',
                  title: t(
                    'api.error.forwardingConfig.forwardingTargetPublicServiceNotFound'
                  )
                });
              },
            'behoerdenclient/problems/illegalForwardingConfig/fitconnectTargetDestinationIsNotActive':
              () => {
                notifyUser({
                  intent: 'error',
                  title: t(
                    'api.error.forwardingConfig.fitConnectTargetDestinationIsNotActive'
                  )
                });
              },
            'behoerdenclient/problems/illegalForwardingConfig/fitconnectTargetDoesNotSupportSchema':
              () => {
                notifyUser({
                  intent: 'error',
                  title: t(
                    'api.error.forwardingConfig.fitConnectTargetDoesNotSupportSchema'
                  )
                });
              },
            'behoerdenclient/problems/forwardingConflict': () => {
              notifyUser({
                intent: 'error',
                title: t('api.error.forwardingConfig.forwardingConflict')
              });
            }
          }
        }
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          BehApiRoutes.fowardingsByPublicServicesByIdAndByOrgUnit(
            variables.orgUnitId,
            variables.destinationConfig.publicServiceIdentifier
          )
        ]
      });
    }
  });
}

export function useOrganizationForwardingPut() {
  const queryClient = useQueryClient();
  const { notifyUserFromRequest } = useNotification();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ destinationConfig, orgUnitId }: CreateDestinationConfig) =>
      notifyUserFromRequest(
        () =>
          behClient.put(
            BehApiRoutes.forwardingsByOrgUnit(orgUnitId),
            destinationConfig
          ),
        {
          errorMessage: t('api.error.createFitConnectSenderCreds'),
          successMessage: t('api.success.createFitConnectSenderCreds')
        }
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          BehApiRoutes.fowardingsByPublicServicesByIdAndByOrgUnit(
            variables.orgUnitId,
            variables.destinationConfig.publicServiceIdentifier
          )
        ]
      });
    }
  });
}

interface DeleteDestinationConfig {
  orgUnitId: string;
  forwardingType: string;
  destinationId: string;
  publicServiceIdentifier: string;
  fromPublicServiceIdentifier: string;
}

export function useOrganizationForwardingDelete() {
  const { notifyUserFromRequest } = useNotification();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      orgUnitId,
      forwardingType,
      publicServiceIdentifier,
      fromPublicServiceIdentifier,
      destinationId
    }: DeleteDestinationConfig) =>
      notifyUserFromRequest(
        () =>
          behClient.delete(BehApiRoutes.forwardingsByOrgUnit(orgUnitId), {
            params: {
              'forwarding-type': forwardingType,
              'public-service-identifier': publicServiceIdentifier,
              'destination-id': destinationId,
              'from-public-service-identifier': fromPublicServiceIdentifier
            }
          }),
        {
          loadingMessage: t('api.loading.deleteDestination'),
          errorMessage: t('api.error.deleteDestination'),
          successMessage: t('api.success.deleteDestination')
        }
      )
  });
}
